import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  FloorRoom,
  GeneralResponse,
  GeneralSettings,
  MenuResponse,
  Order,
  Promotions,
  SoldOutProduct,
} from "../../api/types";
import { useLive } from "../../app/useLive";
import { loadAllergenDictionary } from "../../features/allergens/dictionary";
import { useOrders, type OrdersState } from "../../features/orders/useOrders";
import type { AppEnv } from "../AppShell";
import { appDevice } from "../app-storage";
import { openTablesOf } from "./waiter";

/** Everything the waiter's screens show, kept current by the live channel (as on the till). */
export interface WaiterData {
  menu: MenuResponse | null;
  floor: FloorRoom[];
  orders: OrdersState;
  /** The open order per occupied table. */
  openTables: ReadonlyMap<string, Order>;
  soldOut: ReadonlySet<string>;
  promotions: Promotions | null;
  /** Algemeen: tips, bank-QR for waiters, the IBAN and the receipt's head. */
  general: GeneralSettings | null;
  /** The shop's bank-QR is Bancontact's and confirms itself (ADR 0024). */
  bankQrAutomatic: boolean;
  /** The shop has Bancontact's product "On a Receipt": the QR can go on the bill (ADR 0029). */
  receiptQr: boolean;
}

export function useWaiterData(env: AppEnv): WaiterData {
  const { api, report } = env;
  const orders = useOrders(api, report);

  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const loadMenu = useCallback(() => {
    api.request<MenuResponse>("GET", "/device/menu", { retry: true }).then(setMenu).catch(report);
  }, [api, report]);
  useEffect(loadMenu, [loadMenu]);

  const [floor, setFloor] = useState<FloorRoom[]>([]);
  const loadFloor = useCallback(() => {
    api
      .request<{ rooms: FloorRoom[] }>("GET", "/device/floor", { retry: true })
      .then((answer) => {
        setFloor(answer.rooms);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadFloor, [loadFloor]);

  const [soldOutList, setSoldOutList] = useState<SoldOutProduct[]>([]);
  const loadSoldOut = useCallback(() => {
    api
      .request<{ products: SoldOutProduct[] }>("GET", "/device/sold-out", { retry: true })
      .then((answer) => {
        setSoldOutList(answer.products);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadSoldOut, [loadSoldOut]);

  const [promotions, setPromotions] = useState<Promotions | null>(null);
  const loadPromotions = useCallback(() => {
    api
      .request<{ promotions: Promotions }>("GET", "/device/promotions", { retry: true })
      .then((answer) => {
        setPromotions(answer.promotions);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadPromotions, [loadPromotions]);

  const [general, setGeneral] = useState<GeneralSettings | null>(null);
  const [bankQrAutomatic, setBankQrAutomatic] = useState(false);
  const [receiptQr, setReceiptQr] = useState(false);
  const loadGeneral = useCallback(() => {
    api
      .request<GeneralResponse>("GET", "/device/general", { retry: true })
      .then((answer) => {
        setGeneral(answer.general);
        setBankQrAutomatic(answer.bankQrAutomatic);
        setReceiptQr(answer.receiptQr);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadGeneral, [loadGeneral]);

  useEffect(() => {
    loadAllergenDictionary(api);
  }, [api]);

  useLive(
    appDevice.read() ?? "",
    {
      onMessage: (message) => {
        orders.apply(message);
        switch (message.type) {
          case "menu.updated":
            loadMenu();
            break;
          case "allergens.updated":
            loadAllergenDictionary(api);
            break;
          case "floor.updated":
            loadFloor();
            break;
          case "soldout.updated":
            loadSoldOut();
            break;
          case "promotions.updated":
            loadPromotions();
            break;
          case "general.updated":
            loadGeneral();
            break;
          case "resync":
            loadAllergenDictionary(api);
            loadMenu();
            loadFloor();
            loadSoldOut();
            loadPromotions();
            loadGeneral();
            break;
          default:
            break;
        }
      },
      onRefused: env.onLiveRefused,
    },
    env.session.token,
  );

  const openTables = useMemo(() => openTablesOf(orders.orders), [orders.orders]);
  const soldOut = useMemo(() => new Set(soldOutList.map((product) => product.productId)), [soldOutList]);
  return { menu, floor, orders, openTables, soldOut, promotions, general, bankQrAutomatic, receiptQr };
}
